import { Skeleton } from "@universe/ui/components/skeleton";
import { cn } from "@universe/ui/lib/utils";
import type { ReactNode } from "react";

import {
  LEDGER_ROW_BOX_CLASSES,
  LEDGER_ROW_DIVIDER_CLASSES,
  LEDGER_ROW_LAYOUT_CLASSES,
  LEDGER_ROW_SEPARATOR_CLASSES,
  LEDGER_ROW_SHELL_CLASSES,
} from "@/components/ledger/ledger-row-shell";

interface TransactionListSkeletonProps {
  isEmpty?: boolean;
  emptyMessage?: ReactNode;
  skeletonCount?: number;
  minHeight?: string;
}

/**
 * One bar in a box as tall as the line of text it stands for. With `isEmpty`
 * both are hidden, so an empty placeholder keeps no line heights.
 */
function SkeletonLine({
  isEmpty,
  lineClassName,
  barClassName,
}: {
  isEmpty: boolean;
  lineClassName: string;
  barClassName: string;
}) {
  return (
    <div
      className={cn("flex items-center", lineClassName, isEmpty && "hidden")}
    >
      <Skeleton
        className={cn(barClassName, isEmpty && "animate-none hidden")}
      />
    </div>
  );
}

export default function TransactionListSkeleton({
  isEmpty = false,
  emptyMessage,
  skeletonCount = 8,
  minHeight = "550px",
}: TransactionListSkeletonProps) {
  if (isEmpty && emptyMessage && typeof emptyMessage !== "string") {
    return <div className="w-full pt-6">{emptyMessage}</div>;
  }

  return (
    <div className="w-full" style={{ minHeight }}>
      <div className="mb-2 lg:mb-3">
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "transition-opacity duration-1000 ease-in-out",
              index >= 7 && "hidden md:block"
            )}
            style={{
              opacity: 1 - index * 0.15,
              animationDelay: `${index * 200}ms`,
            }}
          >
            <div className={LEDGER_ROW_SHELL_CLASSES}>
              <div className={LEDGER_ROW_BOX_CLASSES}>
                {/* Each bar sits in a box as tall as the line of text it stands
                    for in a loaded `LedgerEntryItem`, so a placeholder row is
                    as tall as a loaded one and its bars cover that row's text
                    while the two crossfade. */}
                <div className={cn(LEDGER_ROW_LAYOUT_CLASSES, "items-start")}>
                  {/* Left side - Basic info skeleton. Below md it takes the
                      full width, as the loaded info side does, so the amount
                      and date wrap onto their own line. An empty placeholder
                      has no bars to wrap, so it keeps one line. */}
                  <div
                    className={cn(
                      "flex items-center gap-1.5 min-w-0",
                      !isEmpty && "basis-full md:basis-auto"
                    )}
                  >
                    {/* Direction icon skeleton. Below md it stands for the
                        loaded row's one 28px flag, with that flag's margin. */}
                    <Skeleton
                      className={cn(
                        "w-7 h-7 rounded-full mr-1.5 md:w-6 md:h-6 md:mr-0",
                        isEmpty && "animate-none hidden"
                      )}
                    />

                    {/* Currency flag skeleton. From md only: below md the
                        loaded row shows one flag. */}
                    <div className="mr-1.5 hidden md:block">
                      <Skeleton
                        className={cn(
                          "w-[24px] h-[24px] rounded-full",
                          isEmpty && "animate-none hidden"
                        )}
                      />
                    </div>

                    {/* Title and subtitle skeleton. From xl the loaded title
                        and subtitle share a line set by their 32px bullet. */}
                    <div className="flex flex-col xl:flex-row xl:gap-3">
                      <SkeletonLine
                        isEmpty={isEmpty}
                        lineClassName="h-6 md:h-7 xl:h-8"
                        barClassName="h-5 w-32 md:h-[22px]"
                      />
                      <SkeletonLine
                        isEmpty={isEmpty}
                        lineClassName="h-5 xl:h-8"
                        barClassName="h-3 w-20 xl:h-5"
                      />
                    </div>
                  </div>

                  {/* Right side - Amount and date skeleton */}
                  <div className="text-right flex items-center gap-10 ml-auto">
                    <div className="text-right flex flex-row items-end gap-3 md:flex-col md:gap-1 xl:flex-row">
                      {/* Currency amount skeleton */}
                      <SkeletonLine
                        isEmpty={isEmpty}
                        lineClassName="h-6 lg:h-7"
                        barClassName="h-5 w-20 lg:h-6"
                      />

                      <div className={LEDGER_ROW_SEPARATOR_CLASSES} />

                      {/* Date skeleton */}
                      <SkeletonLine
                        isEmpty={isEmpty}
                        lineClassName="h-5 xl:h-6"
                        barClassName="h-4 w-[80px] xl:h-5"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Divider line */}
              <div
                className={cn(
                  LEDGER_ROW_DIVIDER_CLASSES,
                  isEmpty && "animate-none hidden"
                )}
              />
            </div>
          </div>
        ))}

        {isEmpty && emptyMessage && typeof emptyMessage === "string" && (
          <div className="absolute top-[130px] lg:top-[100px] left-1/2 -translate-x-1/2">
            <p className="text-primary text-lg font-medium">{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
}
